import { useState, useRef, useMemo } from "react";
import { Heart, TrendingUp, Globe, BookOpen, Thermometer, Search, Loader2, Users, Zap, Target, ChevronDown, GraduationCap, FileText } from "lucide-react";
import axios from "axios";
import { trackEvent } from "../../lib/track";
import { usePrefetchOrFetch } from "../common/DataPrefetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const UN_SDGS = [
  { n: 1, name: "No Poverty", color: "#E5243B" },
  { n: 2, name: "Zero Hunger", color: "#DDA63A" },
  { n: 3, name: "Good Health & Well-Being", color: "#4C9F38" },
  { n: 4, name: "Quality Education", color: "#C5192D" },
  { n: 5, name: "Gender Equality", color: "#FF3A21" },
  { n: 6, name: "Clean Water & Sanitation", color: "#26BDE2" },
  { n: 7, name: "Affordable & Clean Energy", color: "#FCC30B" },
  { n: 8, name: "Decent Work & Economic Growth", color: "#A21942" },
  { n: 9, name: "Industry, Innovation & Infrastructure", color: "#FD6925" },
  { n: 10, name: "Reduced Inequalities", color: "#DD1367" },
  { n: 11, name: "Sustainable Cities & Communities", color: "#FD9D24" },
  { n: 12, name: "Responsible Consumption & Production", color: "#BF8B2E" },
  { n: 13, name: "Climate Action", color: "#3F7E44" },
  { n: 14, name: "Life Below Water", color: "#0A97D9" },
  { n: 15, name: "Life on Land", color: "#56C02B" },
  { n: 16, name: "Peace, Justice & Strong Institutions", color: "#00689D" },
  { n: 17, name: "Partnerships for the Goals", color: "#19486A" },
];

const SCENARIO_ICONS = {
  gender_equity: Users,
  economic_mobility: TrendingUp,
  climate_action: Thermometer,
  education_access: BookOpen,
  health_equity: Heart,
};

const SCENARIO_COLORS = {
  gender_equity: "#ec4899",
  economic_mobility: "#f59e0b",
  climate_action: "#10b981",
  education_access: "#6366f1",
  health_equity: "#ef4444",
};

const flash = (delay = 0) => ({
  animation: `flashIn 0.55s ease-out ${delay}s both`,
});

export const EquityLens = () => {
  // Scenarios: pulled from the shared prefetch store — no per-mount refetch.
  const [prefetchedScenarios] = usePrefetchOrFetch("equityScenarios", `${API}/equity/scenarios`);
  const scenarios = useMemo(() => prefetchedScenarios || [], [prefetchedScenarios]);

  const [stats, setStats] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [customQuery, setCustomQuery] = useState("");
  const [sdgMapOpen, setSdgMapOpen] = useState(false);

  // Guard against stale responses when the user rapidly clicks multiple scenarios.
  const reqIdRef = useRef(0);

  const explore = async (scenarioId) => {
    const myReq = ++reqIdRef.current;
    setStats(null);
    setResult(null);
    setLoading(true);
    setAnalyzing(false);
    trackEvent("equity_explore", "equity", scenarioId);

    const statsP = axios.post(`${API}/equity/stats`, { scenario: scenarioId }, { timeout: 60000 });
    const exploreP = axios.post(`${API}/equity/explore`, { scenario: scenarioId }, { timeout: 120000 });

    try {
      const r = await statsP;
      if (reqIdRef.current !== myReq) return;
      setStats(r.data);
      setLoading(false);
      setAnalyzing(true);
    } catch {
      if (reqIdRef.current === myReq) setLoading(false);
    }

    try {
      const r = await exploreP;
      if (reqIdRef.current !== myReq) return;
      setResult(r.data);
    } catch {
      if (reqIdRef.current === myReq) setResult({ error: "AI analysis failed. The data above is still accurate." });
    }
    if (reqIdRef.current === myReq) setAnalyzing(false);
  };

  const exploreCustom = async () => {
    if (!customQuery.trim()) return;
    const myReq = ++reqIdRef.current;
    setStats(null);
    setResult(null);
    setLoading(true);
    setAnalyzing(false);
    trackEvent("equity_custom", "equity", customQuery.trim().slice(0, 100));

    const statsP = axios.post(`${API}/equity/stats`, { custom_query: customQuery.trim() }, { timeout: 60000 });
    const exploreP = axios.post(`${API}/equity/explore`, { custom_query: customQuery.trim() }, { timeout: 120000 });

    try {
      const r = await statsP;
      if (reqIdRef.current !== myReq) return;
      setStats(r.data);
      setLoading(false);
      setAnalyzing(true);
    } catch {
      if (reqIdRef.current === myReq) setLoading(false);
    }

    try {
      const r = await exploreP;
      if (reqIdRef.current !== myReq) return;
      setResult(r.data);
    } catch {
      if (reqIdRef.current === myReq) setResult({ error: "AI analysis failed." });
    }
    if (reqIdRef.current === myReq) setAnalyzing(false);
  };

  const d = stats; // data source for DB-driven sections

  return (
    <div className="space-y-6" data-testid="equity-lens">
      <style>{`
        @keyframes flashIn {
          0% { opacity: 0; transform: translateY(18px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseBar {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      `}</style>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl py-10 px-6 md:px-10 text-center"
        style={{ background: "linear-gradient(135deg, #1a0a2e 0%, #0c1a28 40%, #0a1e20 100%)", border: "1px solid rgba(236,72,153,0.15)" }}>
        <p className="text-[10px] font-bold text-pink-400/70 uppercase tracking-[0.25em] mb-3">Equity & Impact Lens</p>
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">Does AI challenge or reinforce structural inequality?</h1>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto mb-4">
          Explore how AI addresses the real challenges facing women, the poor, the climate, the unschooled, and the unhealthy.
          Choose a scenario below or type your own.
        </p>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full cursor-pointer hover:scale-[1.02] transition-transform"
          onClick={() => setSdgMapOpen(!sdgMapOpen)}
          style={{ background: "rgba(0,104,157,0.12)", border: "1px solid rgba(0,104,157,0.2)" }}>
          <Target className="w-3.5 h-3.5 text-sky-400" />
          <span className="text-[10px] font-semibold text-sky-300 tracking-wide">Aligned with the UN Sustainable Development Goals</span>
          <ChevronDown className={`w-3 h-3 text-sky-400 transition-transform duration-300 ${sdgMapOpen ? "rotate-180" : ""}`} />
        </div>
      </div>

      {/* UN SDG Reference Map */}
      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${sdgMapOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="rounded-2xl p-5" data-testid="sdg-reference-map"
          style={{ background: "linear-gradient(135deg, rgba(0,104,157,0.05) 0%, rgba(25,72,106,0.03) 100%)", border: "1px solid rgba(0,104,157,0.1)" }}>
          <p className="text-[10px] font-bold text-sky-400/60 uppercase tracking-[0.2em] mb-3">The 17 Goals</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {UN_SDGS.map((sdg) => (
              <div key={sdg.n} className="flex items-center gap-2 px-2.5 py-2 rounded-lg transition-all hover:scale-[1.03]"
                data-testid={`sdg-ref-${sdg.n}`}
                style={{ background: `${sdg.color}0C`, border: `1px solid ${sdg.color}18` }}>
                <span className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-[10px] font-black text-white"
                  style={{ background: sdg.color }}>{sdg.n}</span>
                <span className="text-[10px] text-slate-300 font-medium leading-tight">{sdg.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scenario Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {scenarios.map((s) => {
          const Icon = SCENARIO_ICONS[s.id] || Globe;
          const color = SCENARIO_COLORS[s.id] || "#94a3b8";
          return (
            <button key={s.id} onClick={() => explore(s.id)} disabled={loading || analyzing}
              data-testid={`equity-${s.id}`}
              className="text-left rounded-xl p-4 hover:scale-[1.02] transition-all disabled:opacity-50 group"
              style={{ background: `${color}0A`, border: `1px solid ${color}25` }}>
              <Icon className="w-5 h-5 mb-2 transition-transform group-hover:scale-110" style={{ color }} />
              <h3 className="text-sm font-bold text-white mb-1">{s.title}</h3>
              <p className="text-[11px] text-slate-400 leading-relaxed mb-2.5">{s.question}</p>
              {s.sdgs?.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid={`equity-sdgs-${s.id}`}>
                  {s.sdgs.slice(0, 4).map((sdg) => (
                    <span key={sdg.number} className="inline-flex items-center justify-center w-5 h-5 rounded text-[8px] font-black text-white shadow-sm"
                      title={sdg.name} style={{ background: sdg.color }}>{sdg.number}</span>
                  ))}
                  {s.sdgs.length > 4 && (
                    <span className="inline-flex items-center justify-center px-1.5 h-5 rounded text-[8px] font-medium text-slate-400 bg-white/[0.06]">
                      +{s.sdgs.length - 4}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Custom Query */}
      <div className="relative">
        <input value={customQuery} onChange={e => setCustomQuery(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") exploreCustom(); }}
          placeholder="Explore your own scenario: 'Can a pregnant woman in rural India access AI that helps her work and stay healthy?'"
          className="w-full px-5 py-4 bg-black/40 border border-white/[0.08] rounded-2xl text-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-pink-500/30 pr-28"
          data-testid="equity-custom-input" />
        <button onClick={exploreCustom} disabled={!customQuery.trim() || loading || analyzing}
          className="absolute top-1/2 -translate-y-1/2 right-3 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-400 hover:to-pink-500 disabled:opacity-30 text-white text-xs font-bold rounded-xl transition-all"
          data-testid="equity-custom-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          Explore
        </button>
      </div>

      {/* Initial Loading */}
      {loading && (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 text-pink-400 animate-spin mx-auto mb-3" />
          <p className="text-xs text-slate-400 animate-pulse">Querying the observatory database</p>
        </div>
      )}

      {/* ===== PHASE 1: DB Stats (flash in instantly) ===== */}
      {d && !d.error && (
        <div className="space-y-4" key={`stats-${d.title}`}>
          {/* Title + Stats */}
          <div className="rounded-2xl p-6" style={{ ...flash(0), background: "rgba(236,72,153,0.04)", border: "1px solid rgba(236,72,153,0.15)" }}>
            <h2 className="text-lg font-bold text-white mb-1" data-testid="equity-result-title">{d.title}</h2>
            <p className="text-xs text-slate-400 mb-4">{d.question}</p>
            <div className="flex flex-wrap gap-6">
              <StatBlock value={d.total_matches?.toLocaleString()} label="AI entities found" color="white" />
              <StatBlock value={`${d.care_pct}%`} label="care-oriented" color="#10b981" />
              <StatBlock value={d.developing_region_count} label="in developing regions" color="#f59e0b" />
              <StatBlock value={d.devices?.length || 0} label="physical devices" color="#3b82f6" />
              {d.research?.total > 0 && <>
                <StatBlock value={d.research.total.toLocaleString()} label="research papers" color="#c084fc" />
                <StatBlock value={`${d.research.care_pct}%`} label="research care %" color="#a78bfa" />
              </>}
            </div>
          </div>

          {/* Stereotype (from scenario data, available in stats) */}
          {(d.stereotype || d.reframe) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={flash(0.12)}>
              {d.stereotype && (
                <div className="bg-red-500/[0.05] border border-red-500/15 rounded-xl p-4">
                  <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-2">The Assumption</p>
                  <p className="text-[13px] text-slate-300 italic leading-relaxed">"{d.stereotype}"</p>
                </div>
              )}
              <div className="bg-emerald-500/[0.05] border border-emerald-500/15 rounded-xl p-4">
                <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider mb-2">The Reframe</p>
                <p className="text-[13px] text-slate-300 leading-relaxed">{d.reframe}</p>
              </div>
            </div>
          )}

          {/* Countries + Industries */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={flash(0.24)}>
            <div className="bg-white/[0.025] border border-white/[0.07] rounded-xl p-5">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Where It's Being Built</p>
              <div className="space-y-2">
                {d.top_countries?.slice(0, 8).map((c, i) => {
                  const maxC = d.top_countries[0]?.count || 1;
                  return (
                    <div key={c.country} className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-300 w-24 truncate font-medium">{c.country}</span>
                      <div className="flex-1 h-2.5 bg-white/[0.04] rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-pink-400/60 transition-all duration-700" style={{ width: `${(c.count / maxC) * 100}%` }} />
                      </div>
                      <span className="text-[11px] text-slate-400 w-8 text-right font-medium">{c.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bg-white/[0.025] border border-white/[0.07] rounded-xl p-5">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Industries Addressing This</p>
              <div className="space-y-2">
                {d.industries?.slice(0, 8).map((ind, i) => {
                  const maxI = d.industries[0]?.count || 1;
                  return (
                    <div key={ind.industry} className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-300 w-28 truncate font-medium">{ind.industry}</span>
                      <div className="flex-1 h-2.5 bg-white/[0.04] rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-violet-400/60 transition-all duration-700" style={{ width: `${(ind.count / maxI) * 100}%` }} />
                      </div>
                      <span className="text-[11px] text-slate-400 w-8 text-right font-medium">{ind.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Care Products + Devices */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={flash(0.36)}>
            <div className="bg-white/[0.025] border border-emerald-500/10 rounded-xl p-5">
              <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider mb-3">Care-Oriented Products Found</p>
              {d.care_products?.length > 0 && (
                <div className="space-y-2">
                  {d.care_products.map((p) => (
                    <div key={p.product_name} className="flex items-start gap-2 bg-white/[0.02] rounded-lg p-2.5">
                      <Heart className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-[12px] font-bold text-white">{p.product_name}</span>
                        <span className="text-[11px] text-slate-400 ml-2">{p.country}</span>
                        <p className="text-[11px] text-slate-400">{(p.tagline || "").slice(0, 70)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white/[0.025] border border-blue-500/10 rounded-xl p-5">
              <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-3">Physical AI in This Space</p>
              {d.devices?.length > 0 ? (
                <div className="space-y-2">
                  {d.devices.slice(0, 5).map((dev) => (
                    <div key={dev.product_name} className="flex items-start gap-2 bg-white/[0.02] rounded-lg p-2.5">
                      <Zap className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-[12px] font-bold text-white">{dev.product_name}</span>
                        <span className="text-[11px] text-slate-400 ml-2">[{dev.form_factor}] {dev.country}</span>
                        <p className="text-[11px] text-slate-400">{(dev.tagline || "").slice(0, 70)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-slate-400">No physical AI devices found for this scenario.</p>
              )}
            </div>
          </div>

          {/* ── Research Intelligence Section ── */}
          {d.research?.total > 0 && (
            <div className="rounded-2xl overflow-hidden" data-testid="research-intelligence-section" style={{ ...flash(0.42), background: "rgba(168,85,247,0.04)", border: "1px solid rgba(168,85,247,0.15)" }}>
              <div className="px-5 pt-5 pb-3 flex items-center gap-2.5">
                <GraduationCap className="w-4 h-4 text-purple-400" />
                <p className="text-[10px] font-bold text-purple-400/80 uppercase tracking-[0.2em]">Research Intelligence</p>
                <span className="text-[9px] px-2 py-0.5 bg-purple-500/15 text-purple-300 rounded-full font-medium">{d.research.total.toLocaleString()} papers</span>
              </div>
              <div className="px-5 pb-5 space-y-4">
                {/* Author Demographics */}
                {d.research.author_composition?.total_known > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Author Gender Demographics</p>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-3 bg-white/[0.04] rounded-full overflow-hidden flex">
                        {d.research.author_composition.female_only > 0 && (
                          <div className="h-full bg-pink-400/70" style={{ width: `${(d.research.author_composition.female_only / d.research.author_composition.total_known * 100)}%` }} title={`Female-only: ${d.research.author_composition.female_only}`} />
                        )}
                        {d.research.author_composition.mixed > 0 && (
                          <div className="h-full bg-purple-400/70" style={{ width: `${(d.research.author_composition.mixed / d.research.author_composition.total_known * 100)}%` }} title={`Mixed: ${d.research.author_composition.mixed}`} />
                        )}
                        <div className="h-full bg-blue-400/40 flex-1" title={`Male-only: ${d.research.author_composition.male_only}`} />
                      </div>
                      <span className="text-[11px] text-purple-300 font-medium whitespace-nowrap">{d.research.author_composition.female_pct}% female/mixed</span>
                    </div>
                    <div className="flex gap-4 mt-1.5 text-[9px] text-slate-500">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-pink-400/70" />Female: {d.research.author_composition.female_only}</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400/70" />Mixed: {d.research.author_composition.mixed}</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400/40" />Male: {d.research.author_composition.male_only}</span>
                    </div>
                  </div>
                )}

                {/* Research Geographic Distribution + Value Orientation */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Research Countries */}
                  {d.research.top_countries?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Research Geography</p>
                      <div className="space-y-1">
                        {d.research.top_countries.slice(0, 5).map((c) => (
                          <div key={c.country} className="flex items-center justify-between py-1 px-2 bg-white/[0.02] rounded">
                            <span className="text-[11px] text-slate-300">{c.country}</span>
                            <span className="text-[10px] text-purple-400 font-medium">{c.count}</span>
                          </div>
                        ))}
                        {d.research.developing_count > 0 && (
                          <p className="text-[9px] text-amber-400 mt-1">{d.research.developing_count} papers from developing countries</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Research Orientations + Sources */}
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Research Orientation</p>
                    <div className="space-y-1">
                      {d.research.orientations && Object.entries(d.research.orientations).sort((a, b) => b[1] - a[1]).map(([orient, count]) => (
                        <div key={orient} className="flex items-center justify-between py-1 px-2 bg-white/[0.02] rounded">
                          <span className="text-[11px] text-slate-300 capitalize">{orient}</span>
                          <span className={`text-[10px] font-medium ${orient === 'care' ? 'text-emerald-400' : orient === 'extraction' ? 'text-red-400' : 'text-slate-400'}`}>{count}</span>
                        </div>
                      ))}
                    </div>
                    {d.research.sources && Object.keys(d.research.sources).length > 0 && (
                      <div className="mt-2">
                        <p className="text-[9px] text-slate-500 mb-1">Sources: {Object.entries(d.research.sources).map(([src, cnt]) => `${src} (${cnt})`).join(', ')}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Top Cited Papers */}
                {d.research.top_papers?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Top Cited Research</p>
                    <div className="space-y-1.5">
                      {d.research.top_papers.slice(0, 4).map((p) => (
                        <div key={p.paper_title} className="flex items-start gap-2 bg-white/[0.02] rounded-lg p-2.5">
                          <FileText className="w-3 h-3 text-purple-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] text-slate-300 truncate">{p.paper_title}</p>
                            <div className="flex gap-2 mt-0.5">
                              <span className="text-[9px] text-slate-500">{p.citations || 0} citations</span>
                              <span className={`text-[9px] capitalize ${p.value_orientation === 'care' ? 'text-emerald-400' : p.value_orientation === 'extraction' ? 'text-red-400' : 'text-slate-500'}`}>{p.value_orientation}</span>
                              {p.country && <span className="text-[9px] text-slate-500">{p.country}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI Analyzing indicator (between Phase 1 and Phase 2) */}
          {analyzing && !result && (
            <div className="flex items-center justify-center gap-3 py-6 rounded-xl" style={{ ...flash(0.48), background: "rgba(236,72,153,0.03)", border: "1px solid rgba(236,72,153,0.08)" }}>
              <div className="flex gap-1">
                {[0, 1, 2, 3, 4].map(i => (
                  <div key={i} className="w-1.5 h-4 rounded-full bg-pink-400/60" style={{ animation: `pulseBar 1.2s ease-in-out ${i * 0.15}s infinite` }} />
                ))}
              </div>
              <p className="text-xs text-slate-400">AI is analyzing the equity landscape and mapping SDG alignment...</p>
            </div>
          )}
        </div>
      )}

      {/* ===== PHASE 2: LLM Narrative (flashes in when ready) ===== */}
      {result && !result.error && (
        <div className="space-y-4" key={`result-${result.title}`}>
          {/* AI Narrative */}
          <div className="rounded-2xl p-6" style={{ ...flash(0), background: "rgba(236,72,153,0.04)", border: "1px solid rgba(236,72,153,0.15)" }}>
            <p className="text-[10px] font-bold text-pink-400/60 uppercase tracking-wider mb-3">AI Analysis</p>
            <p className="text-[13px] text-slate-200 leading-relaxed">{result.narrative}</p>
          </div>

          {/* What the Data Shows (LLM-powered stereotype challenge) */}
          {result.stereotype_challenged && (
            <div className="bg-emerald-500/[0.05] border border-emerald-500/15 rounded-xl p-5" style={flash(0.12)}>
              <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider mb-2">What The Data Shows</p>
              <p className="text-[13px] text-slate-200 leading-relaxed">{result.stereotype_challenged}</p>
            </div>
          )}

          {/* UN SDG Alignment */}
          {result.sdg_alignment?.length > 0 && (
            <div className="rounded-2xl overflow-hidden" data-testid="sdg-alignment-section" style={{ ...flash(0.24), background: "linear-gradient(135deg, rgba(0,104,157,0.06) 0%, rgba(25,72,106,0.04) 100%)", border: "1px solid rgba(0,104,157,0.12)" }}>
              <div className="px-5 pt-5 pb-3 flex items-center gap-2.5">
                <Target className="w-4 h-4 text-sky-400" />
                <p className="text-[10px] font-bold text-sky-400/80 uppercase tracking-[0.2em]">UN Sustainable Development Goals Alignment</p>
              </div>
              <div className="px-5 pb-5 space-y-2.5">
                {result.sdg_alignment.filter(s => s.relevance === "high").map((sdg) => (
                  <div key={`sdg-${sdg.sdg_number}`} className="flex items-start gap-3 rounded-xl p-3.5 transition-all hover:bg-white/[0.02]"
                    data-testid={`sdg-card-${sdg.sdg_number}`}
                    style={{ background: "rgba(255,255,255,0.015)", borderLeft: `3px solid ${sdg.color || '#00689D'}` }}>
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-sm font-black text-white shadow-lg"
                      style={{ background: sdg.color || '#00689D' }}>{sdg.sdg_number}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[12px] font-bold text-white">{sdg.name || `SDG ${sdg.sdg_number}`}</span>
                        <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300 tracking-wider">High</span>
                      </div>
                      <p className="text-[11px] text-slate-400 leading-relaxed">{sdg.connection}</p>
                    </div>
                  </div>
                ))}
                {result.sdg_alignment.filter(s => s.relevance !== "high").length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1.5">
                    {result.sdg_alignment.filter(s => s.relevance !== "high").map((sdg) => (
                      <div key={`sdg-${sdg.sdg_number}`} className="group relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all cursor-default hover:scale-[1.03]"
                        data-testid={`sdg-badge-${sdg.sdg_number}`}
                        style={{ background: `${sdg.color || '#00689D'}10`, border: `1px solid ${sdg.color || '#00689D'}22` }}>
                        <span className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-black text-white"
                          style={{ background: sdg.color || '#00689D' }}>{sdg.sdg_number}</span>
                        <span className="text-[11px] text-slate-300 font-medium">{sdg.name || `SDG ${sdg.sdg_number}`}</span>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-[11px] text-slate-300 w-56 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-20 shadow-xl leading-relaxed">
                          {sdg.connection}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* What Exists + What's Missing (LLM analysis) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={flash(0.36)}>
            <div className="bg-white/[0.025] border border-emerald-500/10 rounded-xl p-5">
              <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider mb-3">What Exists</p>
              <p className="text-[12px] text-slate-300 leading-relaxed">{result.what_exists}</p>
            </div>
            <div className="bg-white/[0.025] border border-red-500/10 rounded-xl p-5">
              <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-3">What's Missing</p>
              <p className="text-[12px] text-slate-300 leading-relaxed">{result.what_is_missing}</p>
            </div>
          </div>

          {/* Research Findings (LLM analysis of research intelligence) */}
          {(result.research_findings || result.research_product_gap) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={flash(0.42)}>
              {result.research_findings && (
                <div className="bg-purple-500/[0.05] border border-purple-500/15 rounded-xl p-5">
                  <p className="text-[10px] font-bold text-purple-400 uppercase tracking-wider mb-3">Research Findings</p>
                  <p className="text-[12px] text-slate-300 leading-relaxed">{result.research_findings}</p>
                </div>
              )}
              {result.research_product_gap && (
                <div className="bg-cyan-500/[0.05] border border-cyan-500/15 rounded-xl p-5">
                  <p className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider mb-3">Research vs Product Gap</p>
                  <p className="text-[12px] text-slate-300 leading-relaxed">{result.research_product_gap}</p>
                </div>
              )}
            </div>
          )}

          {/* Call to Action */}
          {result.call_to_action && (
            <div className="text-center py-6 rounded-xl" style={{ ...flash(0.48), background: "rgba(236,72,153,0.04)", border: "1px solid rgba(236,72,153,0.12)" }}>
              <p className="text-[10px] font-bold text-pink-400 uppercase tracking-wider mb-2">What Should Be Built Next</p>
              <p className="text-[13px] text-white font-medium max-w-3xl mx-auto leading-relaxed">{result.call_to_action}</p>
            </div>
          )}
        </div>
      )}

      {result?.error && <p className="text-sm text-red-400 text-center">{result.error}</p>}
    </div>
  );
};

const StatBlock = ({ value, label, color }) => (
  <div className="text-center" data-testid={`stat-${label?.replace(/\s/g, '-')}`}>
    <p className="text-2xl font-black" style={{ color }}>{value}</p>
    <p className="text-[10px] text-slate-400 font-medium">{label}</p>
  </div>
);

export default EquityLens;
